import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import Layout from 'containers/Layout'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, ContentState, convertToRaw } from 'draft-js'
import htmlToDraft from 'html-to-draftjs'
import draftToHtml from 'draftjs-to-html'
import { trim } from 'lodash'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import Switch from 'components/atoms/Switch'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

const RefundPolicyContentForm = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    active: true,
  })
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [editorStateAr, setEditorStateAr] = useState(EditorState.createEmpty())

  useEffect(() => {
    if (id) {
      fetchRefundPolicyContent()
    }
  }, [id])

  const htmlToEditorState = html => {
    if (!html) return EditorState.createEmpty()
    const contentBlock = htmlToDraft(html)
    if (!contentBlock) return EditorState.createEmpty()
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
    return EditorState.createWithContent(contentState)
  }

  const fetchRefundPolicyContent = async () => {
    try {
      setLoading(true)
      const response = await apiGet(`refund-policy-content/${id}`)
      if (response?.success && response?.data) {
        const data = response.data
        setFormData({
          active: data.active ?? true,
        })
        setEditorState(htmlToEditorState(data.content))
        setEditorStateAr(htmlToEditorState(data.contentAr))
      } else {
        notify.error(t('addRefundPolicyContentModal.messages.fetchFailed'))
        history.push('/master-data?tab=8')
      }
    } catch (error) {
      notify.error(t('addRefundPolicyContentModal.messages.fetchFailed'))
      history.push('/master-data?tab=8')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    const contentText = trim(editorState.getCurrentContent().getPlainText())
    const contentArText = trim(editorStateAr.getCurrentContent().getPlainText())

    if (!contentText) {
      notify.error(t('addRefundPolicyContentModal.messages.contentRequired'))
      return
    }
    if (!contentArText) {
      notify.error(t('addRefundPolicyContentModal.messages.contentArRequired'))
      return
    }

    const payload = {
      content: draftToHtml(convertToRaw(editorState.getCurrentContent())),
      contentAr: draftToHtml(convertToRaw(editorStateAr.getCurrentContent())),
      active: formData.active,
    }

    try {
      setLoading(true)
      const response = isEdit ? await apiPut(`refund-policy-content/${id}`, payload) : await apiPost('refund-policy-content', payload)

      if (response?.success) {
        notify.success(isEdit ? t('addRefundPolicyContentModal.messages.updatedSuccessfully') : t('addRefundPolicyContentModal.messages.addedSuccessfully'))
        history.push('/master-data?tab=8')
      } else {
        notify.error(response?.message || t('addRefundPolicyContentModal.messages.saveFailed'))
      }
    } catch (error) {
      notify.error(t('addRefundPolicyContentModal.messages.saveFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout
      headerRight={
        <div className="mt-4 flex sm:mt-0 gap-3 sm:space-x-3 rtl:space-x-reverse">
          <button
            onClick={() => history.push('/master-data?tab=8')}
            type="button"
            className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:order-1"
          >
            {t('addEditPartner.buttons.goBack')}
          </button>
        </div>
      }
      breadcrumbs={[
        { name: t('masterData.title'), pathname: '/master-data' },
        {
          name: isEdit ? t('masterData.modals.editRefundPolicies') : t('masterData.modals.addNewRefundPolicies'),
          pathname: isEdit ? `/master-data/refund-policy-content/edit/${id}` : '/master-data/refund-policy-content/add',
        },
      ]}
      title={isEdit ? t('masterData.modals.editRefundPolicies') : t('masterData.modals.addNewRefundPolicies')}
    >
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('addRefundPolicyContentModal.labels.content')}</label>
            <Editor
              editorState={editorState}
              wrapperClassName="blog-editor-wrapper"
              editorClassName="blog-editor min-h-[220px]"
              toolbarClassName="blog-toolbar"
              onEditorStateChange={setEditorState}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('addRefundPolicyContentModal.labels.contentAr')}</label>
            <Editor
              editorState={editorStateAr}
              wrapperClassName="blog-editor-wrapper"
              editorClassName="blog-editor min-h-[220px]"
              toolbarClassName="blog-toolbar"
              onEditorStateChange={setEditorStateAr}
            />
          </div>

          <div className="flex items-center justify-between">
            <label htmlFor="active" className="block text-sm font-medium text-gray-700">
              {t('addRefundPolicyContentModal.labels.active')}
            </label>
            <Switch value={formData.active} onChange={value => setFormData(prev => ({ ...prev, active: value }))} />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => history.push('/master-data?tab=8')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              disabled={loading}
            >
              {t('addRefundPolicyContentModal.buttons.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 disabled:opacity-60"
              disabled={loading}
            >
              {loading ? t('addRefundPolicyContentModal.buttons.saving') : isEdit ? t('addRefundPolicyContentModal.buttons.update') : t('addRefundPolicyContentModal.buttons.add')}
            </button>
          </div>
        </div>
      </div>
      <style jsx global>{`
        .blog-editor-wrapper {
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          background: #fff;
        }
        .blog-editor {
          min-height: 220px;
          padding: 1rem;
        }
        .blog-toolbar {
          border-bottom: 1px solid #e5e7eb;
          padding: 0.5rem;
        }
        .rdw-editor-toolbar {
          margin-bottom: 0;
        }
      `}</style>
    </Layout>
  )
}

export default RefundPolicyContentForm
